import { useContext, useEffect, useRef, useState } from "react";
import { CombinedContext } from "../../../context/combinedContext";
import dayjs from "dayjs";
import "dayjs/locale/es";
import isoWeek from "dayjs/plugin/isoWeek";
import translations from "../../../translations/translations";
import companyBasedRequest from "../../../request/companyBasedRequest";
import ExclusionModal from "../exclusionModal/exclusionModal";
import BaseScheduleModal from "../baseScheduleModal/baseScheduleModal";
import scheduleBasedRequest from "../../../request/scheduleBasedRequest";
import { ChevronLeft, ChevronRight, Edit, MoreVertical, Phone, User2 } from "lucide-react";
import CalendarSkeleton from "../calendarSkeleton/calendarSkeleton";
import { useLocation, useNavigate } from "react-router-dom";
import MiniCalendarSkeleton from "../miniCalendarSkeleton/miniCalendarSkeleton";
import WalkinModal from "../walkinModal/walkinModal";
import AddBookingPopup from "../addBookingPopup/addBookingPopup";
import CustomSelect from "../../common/customSelect/customSelect";

dayjs.extend(isoWeek);

const AdminCalendar = () => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const today = dayjs();
    const todayIndex = today.isoWeekday() - 1; // 0 = Monday, 6 = Sunday
    const todayDay = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][todayIndex];

    const [view, setView] = useState("day");
    const [selectedDay, setSelectedDay] = useState(todayDay);
    const [initialLoading, setInitialLoading] = useState(true);
    const [weekLoading, setWeekLoading] = useState(false);
    const [events, setEvents] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [saving, setSaving] = useState(false);
    const [dayIndex, setDayIndex] = useState(todayIndex);
    // const [showBaseScheduleModal, setShowBaseScheduleModal] = useState(false);
    // const [showExclusionModal, setShowExclusionModal] = useState(false);
    const [activeModal, setActiveModal] = useState(false);
    const [weekData, setWeekData] = useState(null);
    const [showControls, setShowControls] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedReassignStaffId, setSelectedReassignStaffId] = useState("");
    const [editingTransit, setEditingTransit] = useState(false);
    const [transitValue, setTransitValue] = useState("");
    const [transitSaving, setTransitSaving] = useState(false);
    const [quickSlot, setQuickSlot] = useState(null);
    const [editingManual, setEditingManual] = useState(false);
    const [manualStartTime, setManualStartTime] = useState("");
    const [manualServices, setManualServices] = useState([]);
    const [manualSaving, setManualSaving] = useState(false);

    // Week navigation
    const [weekStart, setWeekStart] = useState(dayjs().startOf("isoWeek"));
    const weekEnd = dayjs(weekStart).add(6, "day");

    const [popupData, setPopupData] = useState({
        day: "",
        start: "",
        end: "",
        title: "",
        desc: "",
    });

    const cardRef = useRef(null);
    const fabStopRef = useRef(null);
    const calendarRef = useRef(null);
    const [fabMode, setFabMode] = useState("fixed");

    const { userData, setUserData, language } = useContext(CombinedContext);

    const gridRefs = useRef({});

    const role = userData?.user?.role;

    let availableStaff = [];

    const location = useLocation();

    const navigate = useNavigate();

    useEffect(() => {
        if (!fabStopRef.current || !cardRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setFabMode(entry.isIntersecting ? "absolute" : "fixed");
            },
            {
                root: null, // viewport
                threshold: 0,
            }
        );

        observer.observe(fabStopRef.current);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!location.state?.openModal) return;
        setActiveModal(location.state.openModal);
    }, [location.state, navigate]);

    useEffect(() => {
        setSelectedDay(days[dayIndex]);
    }, [dayIndex]);

    useEffect(() => {
        if (!userData) return;

        if (initialLoading) {
            loadWeekData(true);
        } else {
            loadWeekData(false);
        }
    }, [userData, weekStart]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest(".sf-menu-wrapper")) setShowMenu(false);
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    async function loadWeekData(isInitial) {
        try {
            if (isInitial) {
                setInitialLoading(true);
            } else {
                setWeekLoading(true);
            }

            const startStr = weekStart.format("YYYY-MM-DD");

            const shopWeek = await companyBasedRequest.getShopWeekSchedule(
                userData.user.shopId,
                startStr,
                userData.cognitoTokens.AccessToken
            );

            // Build events from staff -> week -> bookings
            const allEvents = [];
            shopWeek?.staff?.forEach((staff) => {
                const staffName = `${staff.firstName || ""} ${staff.lastName || ""}`.trim();

                Object.entries(staff.week || {}).forEach(([dateStr, dayObj]) => {
                    (dayObj.bookings || []).forEach((b) => {
                        allEvents.push({
                            bookingId: b.bookingId,
                            staffId: staff.staffId,
                            staffName,
                            day: dayjs(dateStr).format("ddd"),
                            date: dateStr,
                            start: b.start,
                            end: b.end,
                            startTimeLabel: dayjs(b.start).format("HH:mm"),
                            endTimeLabel: dayjs(b.end).format("HH:mm"),
                            title: `${b.clientName || "Booking"} (${staffName})`,
                            services: b.service || [],
                            isHomeService: b.isHomeService,
                            serviceType: b.serviceType,
                            source: b.source,
                            type: b.type,
                            address: b.address,
                            city: b.city,
                            postalCode: b.postalCode,
                            phone: b.phone,
                            transitMinutes: b.transitMinutes,
                        });
                    });
                });
            });

            setEvents(allEvents);
            setWeekData(shopWeek);
        } catch (err) {
            console.error("Error loading schedule:", err);
        } finally {
            if (isInitial) {
                setInitialLoading(false);
            } else {
                setWeekLoading(false);
            }
        }
    }
    
    const reloadWeek = async () => {
        try {
            const startStr = weekStart.format("YYYY-MM-DD");

            const shopWeek = await companyBasedRequest.getShopWeekSchedule(
                userData.user.shopId,
                startStr,
                userData.cognitoTokens.AccessToken
            );

            // Build events again
            const allEvents = [];
            shopWeek?.staff?.forEach((staff) => {
                const staffName = `${staff.firstName || ""} ${staff.lastName || ""}`.trim();

                Object.entries(staff.week || {}).forEach(([dateStr, dayObj]) => {
                    (dayObj.bookings || []).forEach((b) => {
                        allEvents.push({
                            bookingId: b.bookingId,
                            staffId: staff.staffId,
                            staffName,
                            day: dayjs(dateStr).format("ddd"),
                            date: dateStr,
                            start: b.start,
                            end: b.end,
                            startTimeLabel: dayjs(b.start).format("HH:mm"),
                            endTimeLabel: dayjs(b.end).format("HH:mm"),
                            title: `${b.clientName || "Booking"} (${staffName})`,
                            services: b.service || [],
                            source: b.source,
                        });
                    });
                });
            });

            setWeekData(shopWeek);
            setEvents(allEvents);
        } catch (err) {
            console.error("Failed to reload admin week schedule:", err);
        }
    };

    // Compute dynamic hours based on longest shift across all staff
    const computeHoursForDay = (staffData, selectedDateStr) => {
        if (!staffData || staffData.length === 0) {
            return Array.from({ length: 10 }, (_, i) => 9 + i); // 9–18 fallback
        }

        let earliest = Infinity;
        let latest = -Infinity;

        staffData.forEach((staff) => {
            const dayData = staff.week?.[selectedDateStr];
            if (!dayData) return;

            (dayData.shifts || []).forEach((s) => {
                const [startH] = s.start.split(":").map(Number);
                const [endH] = s.end.split(":").map(Number);

                if (startH < earliest) earliest = startH;
                if (endH > latest) latest = endH;
            });
        });

        if (!isFinite(earliest) || !isFinite(latest) || earliest >= latest) {
            return Array.from({ length: 10 }, (_, i) => 9 + i);
        }

        return Array.from({ length: latest - earliest }, (_, i) => earliest + i);
    };

    const selectedDateStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");
    const hours = computeHoursForDay(weekData?.staff || [], selectedDateStr);

    const addPersonalEvent = async () => {
        const { date, start, end, title, desc } = popupData;

        if (!date || !start || !end) {
            return;
        }

        try {
            setSaving(true);

            await companyBasedRequest.addScheduleExclusion(userData.user.scheduleId, {
                startDate: date,
                endDate: date,
                reason: title || "Unavailable",
                blocks: [{ start, end }],
            });

            setShowPopup(false);
            setSaving(false);

            const startStr = weekStart.format("YYYY-MM-DD");
            const weekData = await companyBasedRequest.getStaffWeekSchedule(
                userData.user.scheduleId,
                startStr,
                userData.cognitoTokens.AccessToken
            );
        } catch (err) {
            console.error(err);
            setSaving(false);
        }
    };

    async function handleAdminCancelBooking() {
        if (!selectedEvent?.bookingId) {
            return;
        }

        try {
            await companyBasedRequest.cancelBookingAdmin(
                selectedEvent.bookingId,
                userData?.cognitoTokens?.AccessToken,
                userData?.user?.role // 👈 send role
            );

            // Remove cancelled booking from UI
            setEvents((prev) => prev.filter((evt) => evt.bookingId !== selectedEvent.bookingId));

            // Also update weekData for accuracy
            const updatedWeek = { ...weekData };
            Object.keys(updatedWeek.staff || {}).forEach((staffKey) => {
                Object.keys(updatedWeek.staff[staffKey].week || {}).forEach((dateKey) => {
                    updatedWeek.staff[staffKey].week[dateKey].bookings = updatedWeek.staff[staffKey].week[
                        dateKey
                    ].bookings?.filter((b) => b.bookingId !== selectedEvent.bookingId);
                });
            });

            setWeekData(updatedWeek);
            setShowDetails(false);
        } catch (err) {
            console.error("Cancel booking failed:", err);
        }
    }

    const reassignBooking = async () => {
        if (!selectedReassignStaffId || !selectedEvent?.bookingId) return;

        try {
            const bookingId = selectedEvent.bookingId;

            await companyBasedRequest.reassignBookingAdmin(
                bookingId,
                selectedReassignStaffId,
                userData?.cognitoTokens?.AccessToken
            );

            // Close popup
            setShowDetails(false);
            setSelectedReassignStaffId("");

            // Reload week schedule
            const startStr = weekStart.format("YYYY-MM-DD");
            const shopWeek = await companyBasedRequest.getShopWeekSchedule(
                userData.user.shopId,
                startStr,
                userData.cognitoTokens.AccessToken
            );

            setWeekData(shopWeek);
        } catch (err) {
            console.error("Reassign failed:", err);
        }
    };

    const getStaffWorkingWindow = (staff, dateStr) => {
        const dayData = staff.week?.[dateStr];
        if (!dayData?.shifts?.length) return null;

        let start = Infinity;
        let end = -Infinity;

        dayData.shifts.forEach((s) => {
            const [sh] = s.start.split(":").map(Number);
            const [eh] = s.end.split(":").map(Number);
            start = Math.min(start, sh);
            end = Math.max(end, eh);
        });

        if (!isFinite(start) || !isFinite(end)) return null;

        return { start, end };
    };

    function formatPhone(number) {
        if (!number) return "";
        // remove all non-digits
        const digits = number.replace(/\D/g, "");

        // US/Canada format (E.164: +1xxxxxxxxxx)
        if (digits.length === 11 && digits.startsWith("1")) {
            return "+1 " + "(" + digits.slice(1, 4) + ") " + digits.slice(4, 7) + "-" + digits.slice(7);
        }

        // fallback (just add + if missing)
        return number.startsWith("+") ? number : "+" + number;
    }

    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setTransitValue(event.transitMinutes ?? "");
        setEditingTransit(false);

        if (event.source === "manual") {
            setManualStartTime(dayjs(event.start).format("HH:mm"));
            setManualServices(event.services || []);
            setEditingManual(false);
        }

        setShowDetails(true);
        setPopupData(event)
    };

    const handleToday = () => {
        const today = dayjs();
        const newWeek = today.startOf("isoWeek");
        const newIndex = today.diff(newWeek, "day");

        setWeekStart(newWeek);
        setDayIndex(newIndex);
        setSelectedDay(days[newIndex]);
    };

    const handleChooseDate = (dateString) => {
        const chosen = dayjs(dateString);
        const newWeek = chosen.startOf("isoWeek");
        const newIndex = chosen.diff(newWeek, "day");

        setWeekStart(newWeek);
        setDayIndex(newIndex);
        setSelectedDay(days[newIndex]);
        setShowDatePicker(false);
    };

    const getMinutes = (t) => {
        const date = dayjs(t); // automatically parses ISO
        return date.hour() * 60 + date.minute();
    };

    const refreshWeek = async () => {
        const startStr = weekStart.format("YYYY-MM-DD");
        const updated = await companyBasedRequest.getShopWeekSchedule(
            userData.user.shopId,
            startStr,
            userData.cognitoTokens.AccessToken
        );
        setWeekData(updated);
    };

    const dayStripRef = useRef(null);

    let touchStartX = 0;
    let touchEndX = 0;

    const onTouchStart = (e) => {
        touchStartX = e.changedTouches[0].clientX;
    };

    const onTouchEnd = (e) => {
        touchEndX = e.changedTouches[0].clientX;
        handleSwipe();
    };

    const handleSwipe = () => {
        const diff = touchEndX - touchStartX;

        // Require a meaningful swipe
        if (Math.abs(diff) < 60) return;

        if (diff < 0) {
            setWeekStart((w) => dayjs(w).add(1, "week"));
            setDayIndex(0);
        } else {
            setWeekStart((w) => dayjs(w).subtract(1, "week"));
            setDayIndex(0);
        }
    };

    const hourHeight = 140;
    const visibleDays = view === "day" ? [selectedDay] : days;

    // ---------------- RENDER ----------------
    if (initialLoading) return <CalendarSkeleton />;

    const todaysDateStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");

    const workingStaff =
        weekData?.staff?.filter((staff) => {
            const dateStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");
            const dayData = staff.week?.[dateStr];

            if (!dayData) return false;

            return dayData.shifts?.length > 0 || dayData.bookings?.length > 0 || dayData.blocks?.length > 0;
        }) || [];

    const staffClass = workingStaff.length <= 1 ? "staff-one" : "staff-multiple";

    const buildInactiveRanges = (staff, dayStr, hours) => {
        const ranges = [];

        const dayData = staff.week?.[dayStr];
        if (!dayData) return ranges;

        const globalStart = hours[0];
        const globalEnd = hours[hours.length - 1] + 1;

        // 1️⃣ Outside working hours
        const staffWindow = getStaffWorkingWindow(staff, dayStr);
        if (staffWindow) {
            if (staffWindow.start > globalStart) {
                ranges.push({
                    startHour: globalStart,
                    endHour: staffWindow.start,
                });
            }

            if (staffWindow.end < globalEnd) {
                ranges.push({
                    startHour: staffWindow.end,
                    endHour: globalEnd,
                });
            }
        }

        // 2️⃣ Exclusions (full day or partial blocks)
        (dayData.blocks || []).forEach((block) => {
            const [sh, sm] = block.start.split(":").map(Number);
            const [eh, em] = block.end.split(":").map(Number);

            let startHour = sh + sm / 60;
            let endHour = eh + em / 60;

            if (staffWindow) {
                // 🔥 Ignore blocks fully outside shift
                if (endHour <= staffWindow.start || startHour >= staffWindow.end) {
                    return;
                }

                // 🔥 Clamp to shift window
                startHour = Math.max(startHour, staffWindow.start);
                endHour = Math.min(endHour, staffWindow.end);
            }

            ranges.push({ startHour, endHour });
        });

        // ✅ 3️⃣ BREAKS (NEW)
        (dayData.breaks || []).forEach((br) => {
            const [sh, sm] = br.start.split(":").map(Number);
            const [eh, em] = br.end.split(":").map(Number);

            const startHour = sh + sm / 60;
            const endHour = eh + em / 60;

            ranges.push({ startHour, endHour });
        });

        return ranges;
    };

    const saveManualBooking = async () => {
        if (!selectedEvent?.bookingId) return;
        console.log(manualServices);

        try {
            setManualSaving(true);

            const newStart = dayjs(`${selectedEvent.date}T${manualStartTime}`).format("YYYY-MM-DDTHH:mm:ss");

            await companyBasedRequest.updateManualBookingAdmin(
                selectedEvent.bookingId,
                {
                    services: manualServices,
                },
                userData.cognitoTokens.AccessToken
            );

            await reloadWeek();

            setEditingManual(false);
            setShowDetails(false);
        } catch (err) {
            console.error("Manual update failed:", err);
            alert("Booking update failed");
        } finally {
            setManualSaving(false);
        }
    };

    const saveTransitTime = async () => {
        if (!selectedEvent?.bookingId) return;

        const minutes = Number(transitValue);
        if (Number.isNaN(minutes) || minutes < 0) return;

        try {
            setTransitSaving(true);

            await companyBasedRequest.updateBookingTransitAdmin(
                selectedEvent.bookingId,
                minutes,
                userData.cognitoTokens.AccessToken
            );

            // Refresh calendar data
            await refreshWeek();

            // Update local selected event
            setSelectedEvent((prev) => (prev ? { ...prev, transitMinutes: minutes } : prev));

            setEditingTransit(false);
        } catch (err) {
            console.error("Failed to update transit time:", err);
            alert(
                language === "es"
                    ? "Conflicto al actualizar el tiempo de traslado"
                    : "Transit time update conflicts with another booking"
            );
        } finally {
            setTransitSaving(false);
        }
    };

    const getValidServicesForSlot = (staff, dayStr, startTime) => {
        const dayData = staff.week?.[dayStr];
        if (!dayData) return [];

        const staffBookings = dayData.bookings || [];
        const staffShifts = dayData.shifts || [];

        const start = dayjs(`${dayStr}T${startTime}`);

        // 1️⃣ Find working window
        let shiftEnd = null;

        staffShifts.forEach((s) => {
            const shiftStart = dayjs(`${dayStr}T${s.start}`);
            const shiftFinish = dayjs(`${dayStr}T${s.end}`);

            // allow exact match at shift start
            if (start.isSame(shiftStart) || (start.isAfter(shiftStart) && start.isBefore(shiftFinish))) {
                shiftEnd = shiftFinish;
            }
        });

        if (!shiftEnd) return [];

        // 2️⃣ Find next booking after clicked time
        const nextBooking = staffBookings
            .map((b) => ({
                start: dayjs(b.start),
                end: dayjs(b.end),
            }))
            .filter((b) => b.start.isAfter(start))
            .sort((a, b) => a.start.diff(b.start))[0];

        const maxEnd = nextBooking ? nextBooking.start : shiftEnd;

        const services = userData.shop.services.filter((svc) => staff.services?.includes(svc.serviceId));

        // 3️⃣ Only allow services that fit completely
        return services.filter((svc) => {
            const duration = svc.durationMinutes || svc.duration || 0;

            const proposedEnd = start.add(duration, "minute");

            // compare using valueOf (safe, no plugins)
            return proposedEnd.valueOf() <= maxEnd.valueOf();
        });
    };

    function formatTimeDisplay(time) {
        if (!time) return "";

        const d = dayjs(time);

        if (d.isValid()) {
            return d.format("h:mm A");
        }

        // Fallback for raw "HH:mm"
        const [h, m] = time.split(":").map(Number);
        const hour12 = h % 12 || 12;
        const minutes = String(m).padStart(2, "0");
        const period = h >= 12 ? "PM" : "AM";

        return `${hour12}:${minutes} ${period}`;
    }

    const isSlotClickable = (staff, dayStr, startTime) => {
        const dayData = staff.week?.[dayStr];
        if (!dayData) return false;

        const start = dayjs(`${dayStr}T${startTime}`);

        // 1️⃣ Must be inside shift
        const insideShift = (dayData.shifts || []).some((s) => {
            const shiftStart = dayjs(`${dayStr}T${s.start}`);
            const shiftEnd = dayjs(`${dayStr}T${s.end}`);
            return start.isSame(shiftStart) || (start.isAfter(shiftStart) && start.isBefore(shiftEnd));
        });

        if (!insideShift) return false;

        // 2️⃣ Cannot overlap booking
        const overlapsBooking = (dayData.bookings || []).some((b) => {
            const bStart = dayjs(b.start);
            const bEnd = dayjs(b.end);
            return start.isSame(bStart) || (start.isAfter(bStart) && start.isBefore(bEnd));
        });

        if (overlapsBooking) return false;

        // 3️⃣ Cannot overlap block
        const overlapsBlock = (dayData.blocks || []).some((block) => {
            const bStart = dayjs(`${dayStr}T${block.start}`);
            const bEnd = dayjs(`${dayStr}T${block.end}`);
            return start.isSame(bStart) || (start.isAfter(bStart) && start.isBefore(bEnd));
        });

        if (overlapsBlock) return false;

        // 4️⃣ Cannot overlap break
        const overlapsBreak = (dayData.breaks || []).some((br) => {
            const brStart = dayjs(`${dayStr}T${br.start}`);
            const brEnd = dayjs(`${dayStr}T${br.end}`);
            return start.isSame(brStart) || (start.isAfter(brStart) && start.isBefore(brEnd));
        });

        if (overlapsBreak) return false;

        return true;
    };

    const isWalkIn = selectedEvent?.source === "manual";

    const hasHomeService = manualServices.some((s) => s.isHomeService === true);

    const hasInShopService = manualServices.some((s) => s.isHomeService !== true);

    const lockedType = hasHomeService ? "home" : hasInShopService ? "shop" : null;

    function canAddService(service) {
        if (!lockedType) return true;
        if (lockedType === "home") return service.isHomeService === true;
        return service.isHomeService !== true;
    }

    const currentStaff = weekData?.staff?.find((s) => s.staffId === selectedEvent?.staffId);

    const staffServiceOptions =
        userData?.shop?.services?.filter((svc) => currentStaff?.services?.includes(svc.serviceId)) || [];

    const getAvailableMinutesForBooking = () => {
        if (!selectedEvent || !weekData) return 0;

        const staff = weekData.staff?.find((s) => s.staffId === selectedEvent.staffId);

        if (!staff) return 0;

        const dayData = staff.week?.[selectedEvent.date];
        if (!dayData) return 0;

        const bookingStart = dayjs(selectedEvent.start);

        let shiftEnd = null;

        // 1️⃣ Find shift window
        (dayData.shifts || []).forEach((s) => {
            const shiftStart = dayjs(`${selectedEvent.date}T${s.start}`);
            const shiftFinish = dayjs(`${selectedEvent.date}T${s.end}`);

            if (
                bookingStart.isSame(shiftStart) ||
                (bookingStart.isAfter(shiftStart) && bookingStart.isBefore(shiftFinish))
            ) {
                shiftEnd = shiftFinish;
            }
        });

        if (!shiftEnd) return 0;

        // 2️⃣ Find next booking
        const nextBooking = (dayData.bookings || [])
            .filter((b) => b.bookingId !== selectedEvent.bookingId)
            .map((b) => dayjs(b.start))
            .filter((start) => start.isAfter(bookingStart))
            .sort((a, b) => a.diff(b))[0];

        // 3️⃣ Find next block
        const nextBlock = (dayData.blocks || [])
            .map((b) => dayjs(`${selectedEvent.date}T${b.start}`))
            .filter((start) => start.isAfter(bookingStart))
            .sort((a, b) => a.diff(b))[0];

        // 4️⃣ Find next break
        const nextBreak = (dayData.breaks || [])
            .map((b) => dayjs(`${selectedEvent.date}T${b.start}`))
            .filter((start) => start.isAfter(bookingStart))
            .sort((a, b) => a.diff(b))[0];

        const candidates = [shiftEnd, nextBooking, nextBlock, nextBreak].filter(Boolean);

        const availableEnd = candidates.sort((a, b) => a.diff(b))[0] || shiftEnd;

        return availableEnd.diff(bookingStart, "minute");
    };

    const maxMinutes = getAvailableMinutesForBooking();

    const currentSelectedDuration = (manualServices || []).reduce((sum, s) => sum + (s.duration || 0), 0);

    const remainingMinutes = maxMinutes - currentSelectedDuration;

    const getAvailableMinutesForSlot = (staff, dayStr, startTime) => {
        const dayData = staff.week?.[dayStr];
        if (!dayData) return 0;

        const bookingStart = dayjs(`${dayStr}T${startTime}`);

        let shiftEnd = null;

        // 1️⃣ Find shift window
        (dayData.shifts || []).forEach((s) => {
            const shiftStart = dayjs(`${dayStr}T${s.start}`);
            const shiftFinish = dayjs(`${dayStr}T${s.end}`);

            if (
                bookingStart.isSame(shiftStart) ||
                (bookingStart.isAfter(shiftStart) && bookingStart.isBefore(shiftFinish))
            ) {
                shiftEnd = shiftFinish;
            }
        });

        if (!shiftEnd) return 0;

        const nextBooking = (dayData.bookings || [])
            .map((b) => dayjs(b.start))
            .filter((start) => start.isAfter(bookingStart))
            .sort((a, b) => a.diff(b))[0];

        const nextBlock = (dayData.blocks || [])
            .map((b) => dayjs(`${dayStr}T${b.start}`))
            .filter((start) => start.isAfter(bookingStart))
            .sort((a, b) => a.diff(b))[0];

        const nextBreak = (dayData.breaks || [])
            .map((b) => dayjs(`${dayStr}T${b.start}`))
            .filter((start) => start.isAfter(bookingStart))
            .sort((a, b) => a.diff(b))[0];

        const candidates = [shiftEnd, nextBooking, nextBlock, nextBreak].filter(Boolean);

        const availableEnd = candidates.sort((a, b) => a.diff(b))[0] || shiftEnd;

        return availableEnd.diff(bookingStart, "minute");
    };

    const validServices = quickSlot
        ? getValidServicesForSlot(quickSlot.staff, quickSlot.date, quickSlot.startTime)
        : [];

    const availableMinutes = quickSlot
        ? getAvailableMinutesForSlot(quickSlot.staff, quickSlot.date, quickSlot.startTime)
        : 0;

    return (
        <div className="sf-card" ref={cardRef}>
            <div className={`sf-layout`}>
                {/* ───────── Sidebar (visible on desktop, slide-in on mobile) ───────── */}

                <main className="sf-main">
                    {/* ───────── Mobile Header ───────── */}
                    {/* ───────── Modern Calendar Header (Booksy style) ───────── */}
                    <div className="sf-topbar-modern">
                        <button className="sf-today-btn" onClick={handleToday}>
                            {translations[language].calendar.toolbar.today}
                        </button>

                        <div className="sf-date-display-large" onClick={() => setShowDatePicker((v) => !v)}>
                            {dayjs(weekStart)
                                .locale(language === "es" ? "es" : "en")
                                .add(dayIndex, "day")
                                .format("dddd, MMM D")}
                        </div>

                        <div className="sf-menu-wrapper">
                            <button
                                className="sf-menu-btn"
                                onClick={() => setShowMenu((prev) => !prev)}
                                aria-label="Menu"
                            >
                                {/* <MoreVertical size={22} strokeWidth={2} /> */}
                                {translations[language].calendar.editSchedule}
                            </button>

                            <div className={`sf-dropdown ${showMenu ? "show" : ""}`}>
                                <button
                                    onClick={() => {
                                        setActiveModal("exclusion");
                                        setShowMenu(false);
                                    }}
                                >
                                    {translations[language].calendar.toolbar.blockTime}
                                </button>
                                <button
                                    onClick={() => {
                                        setActiveModal("baseSchedule");
                                        setShowMenu(false);
                                    }}
                                >
                                    {translations[language].calendar.toolbar.editSchedule}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Inline Slide-Down Calendar */}
                    {showDatePicker && (
                        <div className="sf-inline-calendar">
                            <div className="sf-calendar-header">
                                <button onClick={() => setWeekStart((w) => dayjs(w).subtract(1, "month"))}>‹</button>
                                <div className="sf-month-label">{weekStart.format("MMMM YYYY")}</div>
                                <button onClick={() => setWeekStart((w) => dayjs(w).add(1, "month"))}>›</button>
                            </div>

                            <div className="sf-calendar-grid">
                                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                                    <div key={d} className="sf-cal-dow">
                                        {d}
                                    </div>
                                ))}

                                {/* Render month days */}
                                {Array.from({ length: 31 }).map((_, i) => {
                                    const date = dayjs(weekStart).startOf("month").startOf("week").add(i, "day");
                                    const isCurrent = date.isSame(dayjs(), "day");
                                    const isSelected = date.format("YYYY-MM-DD") === selectedDateStr;

                                    return (
                                        <div
                                            key={i}
                                            className={`sf-cal-day ${isSelected ? "selected" : ""} ${
                                                isCurrent ? "today" : ""
                                            }`}
                                            onClick={() => handleChooseDate(date.format("YYYY-MM-DD"))}
                                        >
                                            {date.date()}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Day Strip — now also triggers calendar when clicked */}
                    <div className="sf-day-strip" ref={dayStripRef} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                        <button
                            className="sf-week-arrow"
                            onClick={() => {
                                setWeekStart((w) => dayjs(w).subtract(1, "week"));
                                setDayIndex(0);
                            }}
                        >
                            <ChevronLeft />
                        </button>
                        {days.map((d, idx) => {
                            const date = dayjs(weekStart).add(idx, "day");
                            const isToday = date.isSame(dayjs(), "day");
                            const isActive = dayIndex === idx;

                            return (
                                <div
                                    key={d}
                                    className={`sf-day-dot${isToday ? " today" : ""}${isActive ? " active" : ""}`}
                                    onClick={() => {
                                        setSelectedDay(d);
                                        setDayIndex(idx);
                                    }}
                                >
                                    <span className="dow">
                                        {date.locale(language === "es" ? "es" : "en").format("ddd")}
                                    </span>
                                    <span className="date-num">{date.format("D")}</span>
                                </div>
                            );
                        })}
                        <button
                            className="sf-week-arrow"
                            onClick={() => {
                                setWeekStart((w) => dayjs(w).add(1, "week"));
                                setDayIndex(0);
                            }}
                        >
                            <ChevronRight />
                        </button>
                    </div>

                    {/* ───────── Calendar Grid ───────── */}
                    {/* ───────── Admin Multi-Staff Calendar ───────── */}

                    <div className="sf-calendar" ref={calendarRef}>
                        <div className="sf-hours-and-staff">
                            {weekLoading && <MiniCalendarSkeleton />}
                            <div className="sf-hours">
                                {hours.map((h) => (
                                    <div key={h} className="sf-hour-label">
                                        {formatTimeDisplay(`${h}:00`)}
                                    </div>
                                ))}
                            </div>

                            <div className={`sf-staff-columns ${staffClass}`}>
                                {weekData?.staff
                                    // Only show staff who are actually working that day
                                    .filter((staff) => {
                                        const dateStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");
                                        const dayData = staff.week?.[dateStr];

                                        if (!dayData) return false;

                                        const works =
                                            (dayData.shifts && dayData.shifts.length > 0) ||
                                            (dayData.bookings && dayData.bookings.length > 0) ||
                                            (dayData.blocks && dayData.blocks.length > 0);

                                        return works;
                                    })
                                    .map((staff, idx) => {
                                        const staffColor = `hsl(${(idx * 70) % 360}, 80%, 90%)`;
                                        const dayStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");
                                        const dayData = staff.week?.[dayStr];
                                        const staffBookings = dayData?.bookings || [];
                                        const gridStartHour = hours[0]; // first visible hour
                                        const gridStartMinutes = gridStartHour * 60;

                                        return (
                                            <div key={staff.staffId} className="sf-staff-column">
                                                {/* Staff header */}
                                                <div className="sf-staff-header">
                                                    {staff.photo ? (
                                                        <img
                                                            className="sf-staff-photo"
                                                            src={staff.photo}
                                                            alt={staff.firstName || "Staff"}
                                                        />
                                                    ) : (
                                                        <div className="sf-staff-placeholder">
                                                            <span>{staff.firstName?.charAt(0).toUpperCase()}</span>
                                                        </div>
                                                    )}
                                                    <div className="sf-staff-name-calendar">
                                                        {staff.firstName} {staff.lastName}
                                                    </div>
                                                </div>

                                                {/* Hour grid */}
                                                <div
                                                    className="sf-hour-grid"
                                                    ref={(el) => {
                                                        if (el) gridRefs.current[staff.staffId] = el;
                                                    }}
                                                >
                                                    {/* Existing hour blocks */}
                                                    {hours.map((h) => (
                                                        <div
                                                            key={h}
                                                            className="sf-hour-block"
                                                            onClick={(e) => {
                                                                const startHour = hours[0];
                                                            
                                                                const gridEl = gridRefs.current[staff.staffId];
                                                                if (!gridEl) return;
                                                            
                                                                const rect = gridEl.getBoundingClientRect();
                                                                const clickY = e.clientY - rect.top + gridEl.scrollTop;
                                                            
                                                                const minutesFromTop = (clickY / hourHeight) * 60;
                                                                const snappedMinutes = Math.round(minutesFromTop / 15) * 15;
                                                            
                                                                const totalMinutes = startHour * 60 + snappedMinutes;
                                                            
                                                                const hh = Math.floor(totalMinutes / 60);
                                                                const mm = totalMinutes % 60;
                                                            
                                                                const startTime = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
                                                            
                                                                if (!isSlotClickable(staff, dayStr, startTime)) return;
                                                            
                                                                setQuickSlot({
                                                                    date: dayStr,
                                                                    startTime,
                                                                    staff,
                                                                    staffId: staff.staffId,
                                                                });
                                                            }}
                                                        />
                                                    ))}

                                                    {buildInactiveRanges(staff, dayStr, hours).map((r, i) => {
                                                        const globalStart = hours[0];
                                                        const gridHeight = hours.length * hourHeight;

                                                        const top = (r.startHour - globalStart) * hourHeight;
                                                        const height = (r.endHour - r.startHour) * hourHeight;

                                                        const clampedTop = Math.max(0, top);
                                                        const clampedHeight = Math.min(height, gridHeight - clampedTop);

                                                        if (clampedHeight <= 0) return null;

                                                        return (
                                                            <div
                                                                key={`inactive-${i}`}
                                                                className="sf-inactive-block"
                                                                style={{
                                                                    top: clampedTop,
                                                                    height: clampedHeight,
                                                                }}
                                                            />
                                                        );
                                                    })}

                                                    {/* Events */}
                                                    {staffBookings.map((b, i) => {
                                                        const bookingWithDate = {
                                                            ...b,
                                                            date: dayStr,
                                                        };

                                                        const startMin = getMinutes(b.start);
                                                        const endMin = getMinutes(b.end);

                                                        const topMinutes = startMin - gridStartMinutes;
                                                        const durationMinutes = endMin - startMin;

                                                        // Clamp so nothing renders above the grid
                                                        const top = Math.max(0, (topMinutes / 60) * hourHeight);
                                                        const height = (durationMinutes / 60) * hourHeight;

                                                        const clampedTop = Math.max(0, top);
                                                        const isShort = height < 55; // tweak as needed

                                                        return (
                                                            <div
                                                                key={i}
                                                                className={`sf-event ${
                                                                    isShort ? "sf-event-short" : ""
                                                                }`}
                                                                style={{
                                                                    top: clampedTop,
                                                                    height,
                                                                    background: staffColor,
                                                                }}
                                                                onClick={() => {
                                                                    const fullEvent = events.find(
                                                                        (e) => e.bookingId === b.bookingId
                                                                    );

                                                                    const merged = {
                                                                        ...fullEvent,
                                                                        ...bookingWithDate, // preserves services
                                                                    };

                                                                    handleEventClick(merged);
                                                                }}
                                                            >
                                                                <div className="sf-event-time-badge">
                                                                    {dayjs(b.start).format("h:mm A")} –{" "}
                                                                    {dayjs(b.end).format("h:mm A")}
                                                                </div>

                                                                <div className="sf-event-info">
                                                                    <div className="sf-event-line">
                                                                        <span className="sf-event-client">
                                                                            {b.clientName || "Booking"}
                                                                        </span>

                                                                        {b.services && (
                                                                            <>
                                                                                <span className="sf-event-separator">
                                                                                    {" "}
                                                                                    —{" "}
                                                                                </span>
                                                                                <span className="sf-event-services-inline">
                                                                                    {Array.isArray(b.services)
                                                                                        ? b.services
                                                                                              .map(
                                                                                                  (s) =>
                                                                                                      s.serviceNameEn ||
                                                                                                      s.serviceNameEs
                                                                                              )
                                                                                              .join(", ")
                                                                                        : b.services}
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                    
                                                </div>
                                            </div>
                                        );
                                    })}
                                {weekData?.staff &&
                                    weekData.staff.filter((staff) => {
                                        const dateStr = dayjs(weekStart).add(dayIndex, "day").format("YYYY-MM-DD");
                                        const dayData = staff.week?.[dateStr];

                                        if (!dayData) return false;

                                        return (
                                            dayData.shifts?.length > 0 ||
                                            dayData.bookings?.length > 0 ||
                                            dayData.blocks?.length > 0
                                        );
                                    }).length === 0 && (
                                        <div className="sf-empty-state">
                                            <p>{translations[language].calendar.empty.noStaffWorking}</p>
                                            <p className="sf-subtle">
                                                {translations[language].calendar.empty.updateHint}
                                            </p>
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>

                    {quickSlot && (
                        <AddBookingPopup
                            date={quickSlot.date}
                            startTime={quickSlot.startTime}
                            staff={quickSlot.staff}
                            services={validServices}
                            maxMinutes={availableMinutes}
                            token={userData.cognitoTokens.AccessToken}
                            language={language}
                            onClose={() => setQuickSlot(null)}
                            onSuccess={(action) => {
                                setQuickSlot(null);
                                reloadWeek();
                                if (action === "openFull") setActiveModal("walkin");
                            }}
                        />
                    )}

                    {/* <button className={`sf-fab ${fabMode}`} onClick={() => setActiveModal("walkin")}>
                        {translations[language].calendar.addWalkIn}
                    </button> */}
                </main>
            </div>
            <div ref={fabStopRef} style={{ height: 1 }} />

            {/* Popup: Add Personal Event */}
            {showPopup && (
                <div className="sf-popup-overlay">
                    <div className="sf-popup">
                        <h3>{translations[language].calendar.popup.addEventTitle}</h3>

                        <div className="sf-popup-section">
                            <label>{translations[language].calendar.popup.title}</label>
                            <input
                                type="text"
                                value={popupData.title}
                                onChange={(e) => setPopupData({ ...popupData, title: e.target.value })}
                                placeholder={translations[language].calendar.popup.placeholderTitle}
                                className="sf-input"
                            />
                        </div>

                        <div className="sf-popup-section sf-time-row">
                            <div className="sf-time-field">
                                <label>{translations[language].calendar.popup.start}</label>
                                <input
                                    type="time"
                                    value={popupData.start}
                                    onChange={(e) => setPopupData({ ...popupData, start: e.target.value })}
                                    className="sf-input"
                                />
                            </div>

                            <div className="sf-time-field">
                                <label>{translations[language].calendar.popup.end}</label>
                                <input
                                    type="time"
                                    value={popupData.end}
                                    onChange={(e) => setPopupData({ ...popupData, end: e.target.value })}
                                    className="sf-input"
                                />
                            </div>
                        </div>

                        <div className="sf-popup-section">
                            <label>{translations[language].calendar.popup.description}</label>
                            <textarea
                                value={popupData.desc}
                                onChange={(e) => setPopupData({ ...popupData, desc: e.target.value })}
                                placeholder={translations[language].calendar.popup.placeholderDesc}
                                className="sf-textarea"
                            />
                        </div>

                        <div className="sf-popup-actions">
                            <button className="btn ghost" onClick={() => setShowPopup(false)}>
                                {translations[language].calendar.popup.cancel}
                            </button>
                            <button className="btn" onClick={addPersonalEvent}>
                                {translations[language].calendar.popup.addEvent}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Popup: Event Details */}
            {/*  */}

            {showDetails && selectedEvent && (
                <div className="sf-popup-overlay">
                    <div className="sf-popup">
                        {(() => {
                            const dateStr = selectedEvent.date;

                            const targetStart = dayjs(selectedEvent.start);
                            const targetEnd = dayjs(selectedEvent.end);

                            const list = (weekData?.staff || []).filter((s) => {
                                if (s.staffId === selectedEvent.staffId) return false;

                                const dayData = s.week?.[dateStr];
                                const staffBookings = dayData?.bookings || [];

                                const hasConflict = staffBookings.some((b) => {
                                    if (b.bookingId === selectedEvent.bookingId) return false;
                                    const bStart = dayjs(b.start);
                                    const bEnd = dayjs(b.end);
                                    return bStart.isBefore(targetEnd) && targetStart.isBefore(bEnd);
                                });

                                return !hasConflict;
                            });

                            availableStaff = list;
                            return null;
                        })()}

                        {/* ================= HEADER ================= */}
                        <div className="sf-detail-header">
                            <h3>{selectedEvent.title}</h3>

                            <div className="sf-detail-time-chip">
                                {dayjs(selectedEvent.start).format("h:mm A")} –{" "}
                                {dayjs(selectedEvent.end).format("h:mm A")}
                            </div>
                        </div>

                        {/* ================= SERVICES ================= */}
                        <div className="sf-detail-section">
                            <div className="sf-section-label sf-services-header">
                                {translations[language].calendar.details.services}

                                {isWalkIn && !editingManual && (
                                    <button
                                        className="sf-inline-edit-btn"
                                        onClick={() => {
                                            setManualServices(selectedEvent.services || []);
                                            setEditingManual(true);
                                        }}
                                    >
                                        <Edit size={14} />
                                    </button>
                                )}
                            </div>

                            {isWalkIn && editingManual ? (
                                <>
                                    <CustomSelect
                                        multi
                                        value={(manualServices || []).map((s) => s.serviceId)}
                                        onChange={(ids) => {
                                            const selected = userData.shop.services
                                                .filter((svc) => ids.includes(svc.serviceId))
                                                .map((svc) => ({
                                                    serviceId: svc.serviceId,
                                                    serviceNameEn: svc.name,
                                                    serviceNameEs: svc.name,
                                                    duration: svc.durationMinutes || svc.duration || 0,
                                                    isHomeService: svc.isHomeService === true,
                                                }));

                                            setManualServices(selected);
                                        }}
                                        placeholder={translations[language].calendar.walkin.service.placeholder}
                                        options={staffServiceOptions.map((s) => {
                                            const duration = s.durationMinutes || s.duration || 0;

                                            const alreadySelected = manualServices.some(
                                                (ms) => ms.serviceId === s.serviceId
                                            );

                                            // If not selected yet and it would exceed remaining time → disable
                                            const exceedsTime = !alreadySelected && duration > remainingMinutes;

                                            return {
                                                value: s.serviceId,
                                                disabled: !canAddService(s) || exceedsTime,
                                                label: (
                                                    <>
                                                        {s.serviceNameEn ||
                                                            s.serviceNameEs ||
                                                            s.nameEn ||
                                                            s.nameEs ||
                                                            s.name ||
                                                            "Service"}
                                                        {s.isHomeService && (
                                                            <>
                                                                <span className="sf-dot"> • </span>
                                                                {language === "es" ? "A domicilio" : "Home service"}
                                                            </>
                                                        )}
                                                        <span className="sf-dot"> • </span>
                                                        {duration} min
                                                        {exceedsTime && (
                                                            <>
                                                                <span className="sf-dot"> • </span>
                                                                <span style={{ opacity: 0.6 }}>
                                                                    {language === "es" ? "No cabe" : "Doesn't fit"}
                                                                </span>
                                                            </>
                                                        )}
                                                    </>
                                                ),
                                            };
                                        })}
                                    />

                                    <div className="sf-inline-actions">
                                        <button className="btn" onClick={saveManualBooking} disabled={manualSaving}>
                                            {manualSaving ? "Saving..." : "Save"}
                                        </button>

                                        <button
                                            className="btn ghost"
                                            onClick={() => {
                                                setEditingManual(false);
                                                setManualServices(selectedEvent.services || []);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="sf-service-list">
                                    {selectedEvent.services && selectedEvent.services.length > 0 ? (
                                        selectedEvent.services.map((s, idx) => (
                                            <div key={idx} className="sf-service-pill">
                                                {s.serviceNameEn || s.serviceNameEs}
                                            </div>
                                        ))
                                    ) : (
                                        <div style={{ opacity: 0.6 }}>
                                            {language === "es" ? "Sin servicios" : "No services"}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* {selectedEvent.services?.length > 0 && (
                            <div className="sf-detail-section">
                                <div className="sf-section-label">
                                    {translations[language].calendar.details.services}
                                </div>

                                <div className="sf-service-list">
                                    {selectedEvent.services.map((s, idx) => (
                                        <div key={idx} className="sf-service-pill">
                                            {s.serviceNameEn || s.serviceNameEs}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )} */}

                        {/* ================= SERVICE TYPE ================= */}
                        <div className="sf-detail-section">
                            <div className="sf-section-label">
                                {selectedEvent.isHomeService
                                    ? language === "es"
                                        ? "Servicio a domicilio"
                                        : "Home service"
                                    : language === "es"
                                    ? "En el local"
                                    : "In-shop"}
                            </div>
                        </div>

                        {/* ================= LOCATION CARD ================= */}
                        {selectedEvent.isHomeService && (
                            <div className="sf-location-card">
                                <div className="sf-location-address">
                                    {selectedEvent.address}
                                    {selectedEvent.city && `, ${selectedEvent.city}`}
                                    {selectedEvent.postalCode && ` ${selectedEvent.postalCode}`}
                                </div>

                                {selectedEvent.phone && (
                                    <div className="sf-location-phone">
                                        <Phone size={14} />
                                        {formatPhone(selectedEvent.phone)}
                                    </div>
                                )}

                                <div className="sf-transit-row">
                                    {editingTransit ? (
                                        <>
                                            <input
                                                type="number"
                                                min="0"
                                                className="sf-transit-input"
                                                value={transitValue}
                                                onChange={(e) => setTransitValue(e.target.value)}
                                            />
                                            <span>min</span>

                                            <button
                                                className="sf-inline-save"
                                                onClick={saveTransitTime}
                                                disabled={transitSaving}
                                            >
                                                {language === "es" ? "Guardar" : "Save"}
                                            </button>

                                            <button
                                                className="sf-inline-cancel"
                                                onClick={() => {
                                                    setEditingTransit(false);
                                                    setTransitValue(selectedEvent.transitMinutes ?? "");
                                                }}
                                            >
                                                {language === "es" ? "Cancelar" : "Cancel"}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <span>
                                                {language === "es"
                                                    ? `Incluye ${selectedEvent.transitMinutes || 0} min de traslado`
                                                    : `Includes ${selectedEvent.transitMinutes || 0} min travel buffer`}
                                            </span>

                                            <button
                                                className="sf-transit-edit-btn"
                                                onClick={() => setEditingTransit(true)}
                                            >
                                                <Edit size={14} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ================= REASSIGN ================= */}
                        <div className="sf-detail-section">
                            <div className="sf-section-label">
                                {translations[language].calendar.details.reassignLabel}
                            </div>

                            {availableStaff.length === 0 && (
                                <div className="sf-no-staff-msg">
                                    {translations[language].calendar.details.noAvailableStaff}
                                </div>
                            )}

                            {availableStaff.length > 0 && (
                                <select
                                    className="sf-input"
                                    value={selectedReassignStaffId}
                                    onChange={(e) => setSelectedReassignStaffId(e.target.value)}
                                >
                                    <option value="">{translations[language].calendar.details.selectStaff}</option>
                                    {availableStaff.map((s) => (
                                        <option key={s.staffId} value={s.staffId}>
                                            {s.firstName} {s.lastName}
                                        </option>
                                    ))}
                                </select>
                            )}

                            <button
                                className="sf-calendar-reassign-btn"
                                disabled={availableStaff.length === 0 || !selectedReassignStaffId}
                                onClick={reassignBooking}
                            >
                                {translations[language].calendar.details.reassign}
                            </button>
                        </div>

                        {/* ================= ACTION BUTTONS ================= */}
                        <div className="sf-popup-actions">
                            <button className="btn ghost" onClick={() => setShowDetails(false)}>
                                {translations[language].calendar.details.close}
                            </button>

                            <button className="btn danger" onClick={handleAdminCancelBooking}>
                                {translations[language].calendar.details.cancelBooking}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            {activeModal === "baseSchedule" && (
                <BaseScheduleModal
                    onClose={() => {
                        setActiveModal(null);
                        reloadWeek();
                    }}
                    staffList={weekData?.staff || []}
                    token={userData.cognitoTokens.AccessToken}
                />
            )}

            {activeModal === "exclusion" && (
                <ExclusionModal
                    onClose={() => setActiveModal(null)}
                    onSaved={reloadWeek}
                    staffList={weekData?.staff || []}
                    token={userData.cognitoTokens.AccessToken}
                    scheduleData={
                        weekData?.staff?.map((s) => {
                            // Build bookingsByDate from week
                            const bookingsByDate = {};
                            Object.entries(s.week || {}).forEach(([dateStr, dayObj]) => {
                                const bookings = (dayObj.bookings || []).map((b) => ({
                                    bookingId: b.bookingId,
                                    staffId: s.staffId,
                                    dateStr,
                                    clientName: b.clientName,
                                    start: b.start,
                                    end: b.end,
                                    services: b.service || b.services || [],
                                    source: b.source,
                                    isHomeService: b.isHomeService,
                                    transitMinutes: b.transitMinutes,
                                }));

                                if (bookings.length) bookingsByDate[dateStr] = bookings;
                            });

                            return {
                                staffId: s.staffId,
                                scheduleId: s.scheduleId,
                                exclusions: s.exclusions || [],
                                bookingsByDate, // ✅ NEW
                            };
                        }) || []
                    }
                    onManageBooking={({ bookingId, dateStr }) => {
                        // Close modal
                        setActiveModal(null);

                        const target = dayjs(dateStr);
                        const newWeek = target.startOf("isoWeek");
                        const newIndex = target.isoWeekday() - 1;

                        // Jump calendar to correct day/week
                        setWeekStart(newWeek);
                        setDayIndex(newIndex);
                        setSelectedDay(days[newIndex]);

                        // Open booking details after state updates + week reload
                        setTimeout(async () => {
                            await reloadWeek();

                            // Find booking in current events (built from shopWeek)
                            const evt = events.find((e) => e.bookingId === bookingId);

                            if (evt) {
                                setSelectedEvent(evt);
                                setTransitValue(evt.transitMinutes ?? "");
                                setEditingTransit(false);
                                setShowDetails(true);
                            } else {
                                // fallback: at least open details with minimal info
                                setSelectedEvent({
                                    bookingId,
                                    date: dateStr,
                                    title: "Booking",
                                });
                                setShowDetails(true);
                            }
                        }, 0);
                    }}
                />
            )}

            {activeModal === "walkin" && (
                <WalkinModal
                    onClose={() => {
                        setActiveModal(null);
                        reloadWeek();
                    }}
                    staffList={weekData?.staff || []}
                    services={userData.shop.services}
                    token={userData.cognitoTokens.AccessToken}
                    week={weekData}
                    selectedDate={selectedDateStr}
                />
            )}
        </div>
    );
};

export default AdminCalendar;
